import React from 'react';
import {Button, Icon} from 'semantic-ui-react';
import {gql} from '@apollo/client';
import {get as _get, has as _has, orderBy as _orderBy, union as _union, isEmpty as _isEmpty} from 'lodash';

import {hideModal} from '@utils/modal';
import {sendAlert} from '@graphql/liveControl/alerts';
import {CreateEventGroupNotification} from '@graphql/notifications/mutation';
import {showMessageBox} from '@utils/messageBox';
import {EVENT_CONTENT_BOOKED_BY_CLIENT, EVENT_CONTENT_OFFERED_BY_CLIENT} from '@constants/variables';
import {convertToInt} from '@utils/helpers';

import apolloClient from '../../../apolloClient';

export const isAtLeastOneRecipientDefined = (clients) => {
    if (clients.length) {
        return clients.find(client => null !== client.notification_email_recipients);
    }

    return false;
};

export const renderEmailPreviewButtons = ({handleBackButtonClick, sending, sendEmail}) => {
    return (
        <Button.Group>
            <Button
                color='grey'
                content='Back to edit'
                icon='angle double left'
                labelPosition='left'
                onClick={handleBackButtonClick}
            />
            <Button
                content='Send'
                type='submit'
                icon={<Icon className='outline' name='mail'/>}
                loading={sending}
                color='blue'
                onClick={sendEmail}
                disabled={sending}
            />
            <Button
                content='Cancel'
                type='button'
                icon='cancel'
                onClick={hideModal}
            />
        </Button.Group>
    );
};

export const sendEmailAlertNotification = (submittedData, isLiveControl) => {
    return apolloClient.mutate({
        mutation: gql`${sendAlert}`,
        variables: submittedData,
        fetchPolicy: 'no-cache',
    }).then(() => {
        hideModal();
        showMessageBox(isLiveControl ? 'liveControl' : 'targetsInfo', 'The alert has been sent to the clients successfully.', null, 'success');
    }).catch((error) => {
        hideModal();
        showMessageBox(isLiveControl ? 'liveControl' : 'targetsInfo', 'The alert could not be sent', error.error, 'error');
    });
};

export const sendEmailGroupNotification = (submittedData) => {
    return apolloClient.mutate({
        mutation: CreateEventGroupNotification,
        variables: submittedData,
        fetchPolicy: 'no-cache',
    }).then(() => {
        hideModal();
        showMessageBox('eventsIndex', 'The notification has been sent to the clients successfully.', null, 'success');
    }).catch((error) => {
        hideModal();
        showMessageBox('eventsIndex', 'The notification could not be sent', error.error, 'error', true);
    });
};

export const getClientsFromDistributions = (distributions, recipientTypes, clientDistributionsObject) => {
    let clientsSelection = [];
    let bookedClients = [];
    let offeredClients = [];

    distributions.forEach((distribution) => {
        recipientTypes.forEach((recipient_type) => {
            let clients = _get(clientDistributionsObject, `${recipient_type}.1.${distribution}`, []);

            if (recipient_type === EVENT_CONTENT_BOOKED_BY_CLIENT) {
                bookedClients = _union(bookedClients, clients);
            } else if (recipient_type === EVENT_CONTENT_OFFERED_BY_CLIENT) {
                offeredClients = _union(offeredClients, clients);
            }

            clientsSelection = _union(clientsSelection, clients);
        });
    });

    return {clientsSelection, bookedClients, offeredClients};
};

export const setDistributions = (clientsList, distributionsObject = {}, key = 'bookings') => {
    clientsList.forEach((client) => {
        (client[key] || []).forEach((item) => {
            if (!_has(distributionsObject, `${item.distribution_type}.${item.product}`)) {
                distributionsObject[item.distribution_type] = {
                    ...distributionsObject[item.distribution_type],
                    [item.product]: [],
                };
            }

            let list = distributionsObject[item.distribution_type][item.product];

            distributionsObject[item.distribution_type][item.product] = _union(list, [client.id]);
        });
    });

    return distributionsObject;
};

export const parseDistributionsForList = (options) => {
    return _orderBy(
        options,
        ['product.id', 'distribution_type.id'],
        ['ASC', 'ASC']
    ).map((distribution) => {
        let productId = convertToInt(distribution.product.id);

        return {
            key: distribution.id,
            text: `${distribution.product.short_name} - ${distribution.distribution_type.name}`,
            value: productId,
        };
    });
};

export const hasNewRecipientTypeClients = (prevTypes, currentTypes, bookedClients = [], offeredClients = []) => {
    const newTypes = currentTypes.filter(type => !prevTypes.includes(type));

    return newTypes.some(type => {
        if (type === EVENT_CONTENT_BOOKED_BY_CLIENT) {
            return !_isEmpty(bookedClients);
        }

        if (type === EVENT_CONTENT_OFFERED_BY_CLIENT) {
            return !_isEmpty(offeredClients);
        }

        return false;
    });
};
